import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import ContextualTour from '@/components/feature/ContextualTour';
import { supabase } from '@/lib/supabase';
import {
  useCatalogo,
  type CategoriaInfraestrutura,
  type ItemDeInfraestrutura,
} from '@/lib/catalogo';
import {
  criarTorre as criarTorreNoBanco,
  gerarUnidades,
  resumirGeracao,
  validarEstrutura,
  vincularEquipamentos,
  SELECT_TORRE,
  type EstruturaDaTorre,
} from '@/lib/torresRepo';
import {
  interpretarErroDeLimite,
  invalidarUsoDoPlano,
  mensagemDeUpgrade,
} from '@/lib/planoLimites';

interface TorreRecord {
  id: string;
  condominio_id: string;
  nome: string;
  pavimentos: number;
  aptos_por_andar: number;
  area_construida: number;
  ativo: boolean;
  andares: Array<{ andar: number; unidades: number; descricao: string }>;
  pavimentos_com_unidades: number | null;
  primeiro_pavimento_com_unidades: number;
  unidades_geradas_em: string | null;
}

const CLASSE_CAMPO =
  'w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm ' +
  'focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300';

const NOVA_TORRE = {
  nome: '',
  pavimentos: 1,
  aptos_por_andar: 0,
  area_construida: 0,
  primeiro_pavimento: 1,
  pavimentos_com_unidades: 0,
};

const torresTourSteps = [
  {
    title: 'Gestão de Torres',
    description:
      'Cada condomínio pode ter múltiplas torres ou blocos. Aqui você cadastra e configura cada uma delas com suas características físicas.',
    icon: 'ri-building-4-line',
  },
  {
    title: 'Adicionar nova torre',
    description:
      'Clique em "Nova Torre" para cadastrar um bloco. Defina nome, número de pavimentos, apartamentos por andar e área construída.',
    icon: 'ri-add-circle-line',
  },
  {
    title: 'Ativar e Inativar Torres',
    description:
      'Use o ícone de olho na coluna de ações para desativar uma torre temporariamente. Torres inativas deixam de contar nos acompanhamentos e relatórios do condomínio, mas todos os dados são preservados.',
    icon: 'ri-eye-off-line',
  },
  {
    title: 'Configurar andares',
    description:
      'Clique no ícone de engrenagem ao lado de cada torre para acessar a configuração detalhada de andares. Lá você adiciona cada andar com seu número de unidades e descrição.',
    icon: 'ri-settings-3-line',
  },
  {
    title: 'Excluir torre',
    description:
      'Se precisar remover uma torre, use o ícone de lixeira. Atenção: todos os andares configurados serão perdidos.',
    icon: 'ri-delete-bin-line',
  },
];

export default function TorresTab() {
  const { id: condoId } = useParams<{ id: string }>();
  const [torres, setTorres] = useState<TorreRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mostrarInativas, setMostrarInativas] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TorreRecord | null>(null);
  const [criando, setCriando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [novaTorre, setNovaTorre] = useState({ ...NOVA_TORRE });
  // Equipamentos escolhidos no cadastro: viram linhas de `infraestrutura` da
  // torre assim que ela existe, e não uma lista paralela.
  const [equipamentos, setEquipamentos] = useState<string[]>([]);
  const [confirmandoGeracao, setConfirmandoGeracao] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'info' | 'error' } | null>(
    null,
  );

  const showToast = (msg: string, type: 'success' | 'info' | 'error' = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const { itens: categoriasDeInfra } = useCatalogo<CategoriaInfraestrutura>(
    'infraestrutura_categorias',
  );
  const { itens: itensDoCatalogo } = useCatalogo<ItemDeInfraestrutura>('infraestrutura_itens');

  // A estrutura que a tela mostra e que a geração recebe são a mesma coisa.
  const estrutura: EstruturaDaTorre = {
    pavimentos: novaTorre.pavimentos,
    primeiroPavimento: novaTorre.primeiro_pavimento,
    pavimentosComUnidades: novaTorre.pavimentos_com_unidades,
    unidadesPorPavimento: novaTorre.aptos_por_andar,
  };
  const problemasDaEstrutura = validarEstrutura(estrutura);
  const resumo = resumirGeracao(estrutura);
  const podeGerar = problemasDaEstrutura.length === 0 && !!resumo;

  const equipamentosPorCategoria = useMemo(() => {
    const porCategoria = new Map<string, ItemDeInfraestrutura[]>();
    itensDoCatalogo.forEach((item) => {
      const lista = porCategoria.get(item.categoria_id) ?? [];
      lista.push(item);
      porCategoria.set(item.categoria_id, lista);
    });
    return categoriasDeInfra
      .map((categoria) => ({ categoria, itens: porCategoria.get(categoria.id) ?? [] }))
      .filter((grupo) => grupo.itens.length > 0);
  }, [categoriasDeInfra, itensDoCatalogo]);

  const alternarEquipamento = (id: string) =>
    setEquipamentos((atual) =>
      atual.includes(id) ? atual.filter((i) => i !== id) : [...atual, id],
    );

  const fecharFormulario = () => {
    setNovaTorre({ ...NOVA_TORRE });
    setEquipamentos([]);
    setConfirmandoGeracao(false);
    setCriando(false);
  };

  /**
   * Cria a torre e, quando pedido, gera as unidades na mesma ação.
   *
   * A geração é uma chamada separada de propósito: quem recusa a segunda
   * execução é o banco (migration 0053), e a torre precisa existir para ser
   * carimbada. Se a geração falhar, a torre fica criada — sem unidades e com o
   * botão disponível na tela de configuração, que é melhor do que desfazer um
   * cadastro que a pessoa acabou de preencher.
   */
  const criarTorre = async (comUnidades: boolean) => {
    if (!condoId || !novaTorre.nome.trim()) return;
    if (comUnidades && !podeGerar) return;

    setSalvando(true);
    try {
      const torre = await criarTorreNoBanco({
        condominioId: condoId,
        nome: novaTorre.nome,
        pavimentos: novaTorre.pavimentos,
        unidadesPorPavimento: novaTorre.aptos_por_andar,
        areaConstruida: novaTorre.area_construida || null,
        primeiroPavimento: novaTorre.primeiro_pavimento,
        pavimentosComUnidades: novaTorre.pavimentos_com_unidades || null,
      });

      const escolhidos = itensDoCatalogo.filter((item) => equipamentos.includes(item.id));
      if (escolhidos.length > 0) {
        try {
          await vincularEquipamentos(condoId, torre.id, escolhidos);
        } catch (e: any) {
          // A torre existe: o equipamento que faltou se cadastra na aba
          // Infraestrutura, e dizer isso é melhor do que fingir sucesso.
          showToast(`Torre criada, mas os equipamentos falharam: ${e.message}`, 'error');
        }
      }

      if (comUnidades) {
        const gerado = await gerarUnidades(torre.id, estrutura);
        invalidarUsoDoPlano();
        showToast(
          `Torre "${torre.nome}" criada com ${gerado.criadas} unidade(s): ${gerado.primeira} a ${gerado.ultima}.`,
          'success',
        );
      } else {
        showToast(`Torre "${torre.nome}" cadastrada.`, 'success');
      }

      fecharFormulario();
      await fetchTorres();
    } catch (e: any) {
      const limite = interpretarErroDeLimite(e);
      showToast(limite ? mensagemDeUpgrade(limite) : `Erro: ${e.message}`, 'error');
      setConfirmandoGeracao(false);
    } finally {
      setSalvando(false);
    }
  };

  const fetchTorres = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fErr } = await supabase
        .from('torres')
        .select(SELECT_TORRE)
        .eq('condominio_id', condoId)
        .order('nome', { ascending: true });

      if (fErr) setError(fErr.message);
      else setTorres((data || []) as TorreRecord[]);
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar torres');
    } finally {
      setLoading(false);
    }
  }, [condoId]);

  useEffect(() => {
    if (condoId) void fetchTorres();
  }, [condoId, fetchTorres]);

  const toggleAtivo = async (id: string) => {
    const torre = torres.find((t) => t.id === id);
    if (!torre) return;
    const novoAtivo = !torre.ativo;

    const { error: uErr } = await supabase.from('torres').update({ ativo: novoAtivo }).eq('id', id);

    if (uErr) {
      showToast(`Erro: ${uErr.message}`, 'error');
      return;
    }

    setTorres((prev) => prev.map((t) => (t.id === id ? { ...t, ativo: novoAtivo } : t)));
    showToast(
      `"${torre.nome}" ${novoAtivo ? 'reativada' : 'desativada'} com sucesso.`,
      novoAtivo ? 'success' : 'info',
    );
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { error: dErr } = await supabase.from('torres').delete().eq('id', deleteTarget.id);

    if (dErr) {
      showToast(`Erro: ${dErr.message}`, 'error');
      setDeleteTarget(null);
      return;
    }

    setTorres((prev) => prev.filter((t) => t.id !== deleteTarget.id));
    showToast(`"${deleteTarget.nome}" excluída com sucesso.`, 'success');
    setDeleteTarget(null);
  };

  const torresAtivas = torres.filter((t) => t.ativo !== false);
  const torresInativas = torres.filter((t) => t.ativo === false);
  const listaVisivel = mostrarInativas ? torres : torresAtivas;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 py-12">
        <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
          <i className="ri-error-warning-line text-red-500"></i>
        </div>
        <p className="text-sm text-foreground-600">{error}</p>
        <button
          onClick={fetchTorres}
          className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 whitespace-nowrap"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-foreground-600">
          {torresAtivas.length} torre(s) ativa(s)
          {torresInativas.length > 0 ? ` — ${torresInativas.length} inativa(s)` : ''}
        </p>
        <button
          type="button"
          onClick={() => setCriando(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          Nova Torre
        </button>
      </div>

      {torresInativas.length > 0 && (
        <div className="mb-4">
          <label className="inline-flex items-center gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={mostrarInativas}
              onChange={(e) => setMostrarInativas(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-5 h-5 rounded-md border-2 border-background-300 flex items-center justify-center shrink-0 transition-all duration-200 peer-checked:bg-primary-500 peer-checked:border-primary-500">
              <i className="ri-check-line text-background-50 text-[10px] leading-none scale-0 peer-checked:scale-100 transition-transform duration-200"></i>
            </div>
            <span className="text-sm text-foreground-600">
              Mostrar torres inativas ({torresInativas.length})
            </span>
          </label>
        </div>
      )}

      {mostrarInativas && torresInativas.length > 0 && (
        <div className="p-4 bg-amber-50/60 border border-amber-200/60 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-5 h-5 flex items-center justify-center text-amber-600">
              <i className="ri-error-warning-line"></i>
            </div>
            <p className="text-sm font-medium text-amber-800">
              Torres inativas ({torresInativas.length})
            </p>
          </div>
          <p className="text-xs text-amber-700 ml-7">
            Torres inativas deixam de contar nos acompanhamentos e relatórios. Seus dados (andares,
            unidades) continuam preservados.
          </p>
        </div>
      )}

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-background-200/70 bg-background-100/50">
                <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                  Torre
                </th>
                <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                  Pavimentos
                </th>
                <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                  Unid./Pavimento
                </th>
                <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                  Área (m²)
                </th>
                <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                  Andares Configurados
                </th>
                <th className="text-center text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                  Status
                </th>
                <th className="text-right text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200/50">
              {listaVisivel.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-sm text-foreground-400">
                    Nenhuma torre cadastrada.
                  </td>
                </tr>
              ) : (
                listaVisivel.map((torre) => {
                  const isAtiva = torre.ativo !== false;
                  return (
                    <tr
                      key={torre.id}
                      className={`hover:bg-background-100/30 transition-colors ${!isAtiva ? 'opacity-55 bg-amber-50/20' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isAtiva ? 'bg-primary-50' : 'bg-amber-100'}`}
                          >
                            <i
                              className={`${isAtiva ? 'ri-building-4-line text-primary-600' : 'ri-building-4-line text-amber-500'} text-sm`}
                            ></i>
                          </div>
                          <div className="min-w-0">
                            <span
                              className={`text-sm font-semibold ${isAtiva ? 'text-foreground-800' : 'text-foreground-500'}`}
                            >
                              {torre.nome}
                            </span>
                            {torre.unidades_geradas_em && (
                              <span className="block text-[11px] text-foreground-400">
                                unidades geradas
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-foreground-700">{torre.pavimentos}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-foreground-700">{torre.aptos_por_andar}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-foreground-700">
                          {torre.area_construida?.toLocaleString('pt-BR') || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-foreground-600">
                          {torre.andares?.length || 0} andar(es)
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {isAtiva ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>Ativa
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>Inativa
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => toggleAtivo(torre.id)}
                            className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${isAtiva ? 'text-foreground-500 hover:bg-amber-50 hover:text-amber-600' : 'text-amber-600 hover:bg-green-50 hover:text-green-600'}`}
                            title={isAtiva ? 'Desativar torre' : 'Reativar torre'}
                          >
                            <i
                              className={`${isAtiva ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}
                            ></i>
                          </button>
                          <Link
                            to={`/condominios/${condoId}/torres/${torre.id}`}
                            className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-primary-600 transition-colors"
                            title="Configurar torre"
                          >
                            <i className="ri-settings-3-line text-sm"></i>
                          </Link>
                          <button
                            onClick={() => setDeleteTarget(torre)}
                            className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-500 hover:bg-background-100 hover:text-red-600 transition-colors"
                            title="Excluir"
                          >
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {criando && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-foreground-900/40"
            onClick={() => setCriando(false)}
            aria-hidden="true"
          ></div>
          <div className="relative bg-background-50 border border-background-200/70 rounded-lg w-full max-w-lg shadow-lg">
            <div className="p-6 pb-4 border-b border-background-200/70">
              <h3 className="text-base font-semibold text-foreground-900">Nova Torre</h3>
              <p className="text-sm text-foreground-500 mt-1">
                Cadastre um bloco do condomínio. Os andares podem ser configurados depois.
              </p>
            </div>

            <form
              onSubmit={(evento) => {
                evento.preventDefault();
                void criarTorre(false);
              }}
              className="p-6 pt-4 flex flex-col gap-6 max-h-[70vh] overflow-y-auto"
            >
              {/* identificacao */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2">
                  <label
                    htmlFor="torre_nome"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Nome da torre *
                  </label>
                  <input
                    id="torre_nome"
                    type="text"
                    required
                    value={novaTorre.nome}
                    onChange={(e) => setNovaTorre({ ...novaTorre, nome: e.target.value })}
                    placeholder="Ex: Torre A, Bloco 1"
                    className={CLASSE_CAMPO}
                  />
                </div>
                <div>
                  <label
                    htmlFor="torre_area"
                    className="block text-sm font-medium text-foreground-700 mb-1.5"
                  >
                    Área (m²)
                  </label>
                  <input
                    id="torre_area"
                    type="number"
                    min={0}
                    value={novaTorre.area_construida}
                    onChange={(e) =>
                      setNovaTorre({ ...novaTorre, area_construida: parseInt(e.target.value) || 0 })
                    }
                    className={CLASSE_CAMPO}
                  />
                </div>
              </div>

              {/* estrutura */}
              <fieldset className="border border-background-200/70 rounded-lg p-4">
                <legend className="px-2 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                  Estrutura
                </legend>
                <p className="text-xs text-foreground-500 mb-4">
                  Nem todo pavimento tem apartamento: térreo com salão de festas e cobertura com
                  casa de máquinas contam como pavimento e não geram unidade.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="torre_pavimentos"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      Total de pavimentos
                    </label>
                    <input
                      id="torre_pavimentos"
                      type="number"
                      min={1}
                      value={novaTorre.pavimentos}
                      onChange={(e) =>
                        setNovaTorre({ ...novaTorre, pavimentos: parseInt(e.target.value) || 1 })
                      }
                      className={CLASSE_CAMPO}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="torre_pav_unidades"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      Pavimentos com unidades
                    </label>
                    <input
                      id="torre_pav_unidades"
                      type="number"
                      min={0}
                      max={novaTorre.pavimentos}
                      value={novaTorre.pavimentos_com_unidades}
                      onChange={(e) =>
                        setNovaTorre({
                          ...novaTorre,
                          pavimentos_com_unidades: parseInt(e.target.value) || 0,
                        })
                      }
                      className={CLASSE_CAMPO}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="torre_primeiro_pav"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      A partir do pavimento
                    </label>
                    <input
                      id="torre_primeiro_pav"
                      type="number"
                      min={1}
                      value={novaTorre.primeiro_pavimento}
                      onChange={(e) =>
                        setNovaTorre({
                          ...novaTorre,
                          primeiro_pavimento: parseInt(e.target.value) || 1,
                        })
                      }
                      className={CLASSE_CAMPO}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="torre_aptos"
                      className="block text-sm font-medium text-foreground-700 mb-1.5"
                    >
                      Unidades por pavimento
                    </label>
                    <input
                      id="torre_aptos"
                      type="number"
                      min={0}
                      value={novaTorre.aptos_por_andar}
                      onChange={(e) =>
                        setNovaTorre({
                          ...novaTorre,
                          aptos_por_andar: parseInt(e.target.value) || 0,
                        })
                      }
                      className={CLASSE_CAMPO}
                    />
                  </div>
                </div>

                {resumo && problemasDaEstrutura.length === 0 && (
                  <p
                    className="mt-4 flex items-start gap-2 text-xs text-primary-700 bg-primary-50/70 border border-primary-200/70 rounded-md px-3 py-2.5"
                    aria-live="polite"
                  >
                    <i className="ri-home-4-line mt-px"></i>
                    <span>
                      <strong>{resumo.total} unidade(s)</strong> serão geradas, de{' '}
                      <strong>{resumo.primeira}</strong> a <strong>{resumo.ultima}</strong>.
                    </span>
                  </p>
                )}

                {problemasDaEstrutura.length > 0 &&
                  novaTorre.pavimentos_com_unidades > 0 &&
                  novaTorre.aptos_por_andar > 0 && (
                    <ul className="mt-4 flex flex-col gap-1" aria-live="polite">
                      {problemasDaEstrutura.map((problema) => (
                        <li
                          key={`${problema.campo}-${problema.mensagem}`}
                          className="flex items-start gap-2 text-xs text-amber-700"
                        >
                          <i className="ri-error-warning-line mt-px"></i>
                          {problema.mensagem}
                        </li>
                      ))}
                    </ul>
                  )}
              </fieldset>

              {/* equipamentos */}
              <fieldset className="border border-background-200/70 rounded-lg p-4">
                <legend className="px-2 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
                  Equipamentos e áreas
                </legend>
                <p className="text-xs text-foreground-500 mb-4">
                  O que existe nesta torre — estacionamento, salão de festas, elevadores. Cada item
                  escolhido vira um registro na aba Infraestrutura.
                </p>

                {equipamentosPorCategoria.length === 0 ? (
                  <p className="text-xs text-foreground-500">
                    O catálogo de infraestrutura ainda não tem itens. Os equipamentos podem ser
                    cadastrados depois, na aba Infraestrutura.
                  </p>
                ) : (
                  <div className="flex flex-col gap-4 max-h-56 overflow-y-auto pr-1">
                    {equipamentosPorCategoria.map(({ categoria, itens }) => (
                      <div key={categoria.id}>
                        <p className="text-xs font-semibold text-foreground-600 mb-2">
                          {categoria.nome}
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                          {itens.map((item) => (
                            <label
                              key={item.id}
                              className="inline-flex items-center gap-2.5 cursor-pointer select-none text-sm text-foreground-700 rounded-md px-2 py-1.5 hover:bg-background-100/70"
                            >
                              <input
                                type="checkbox"
                                checked={equipamentos.includes(item.id)}
                                onChange={() => alternarEquipamento(item.id)}
                                className="sr-only peer"
                              />
                              <span className="w-4 h-4 rounded border-2 border-background-300 flex items-center justify-center shrink-0 transition-colors peer-checked:bg-primary-500 peer-checked:border-primary-500 peer-focus-visible:ring-2 peer-focus-visible:ring-primary-300">
                                <i className="ri-check-line text-background-50 text-[9px] leading-none scale-0 peer-checked:scale-100 transition-transform"></i>
                              </span>
                              {item.nome}
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {equipamentos.length > 0 && (
                  <p className="text-xs text-foreground-500 mt-3">
                    {equipamentos.length} item(ns) selecionado(s).
                  </p>
                )}
              </fieldset>

              <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-background-200/70">
                <button
                  type="button"
                  onClick={fecharFormulario}
                  className="px-4 py-2.5 rounded-md border border-background-300 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={salvando || !novaTorre.nome.trim()}
                  className="px-4 py-2.5 rounded-md border border-primary-300 text-sm font-semibold text-primary-700 hover:bg-primary-50 transition-colors disabled:opacity-60"
                >
                  {salvando ? 'Salvando...' : 'Criar sem unidades'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmandoGeracao(true)}
                  disabled={salvando || !novaTorre.nome.trim() || !podeGerar}
                  title={
                    podeGerar
                      ? undefined
                      : 'Informe pavimentos com unidades e unidades por pavimento.'
                  }
                  className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  Gerar unidades
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* A frase e a exigencia do produto: gerar unidades nao tem segunda chance. */}
      {confirmandoGeracao && resumo && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center px-4"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="titulo_confirmar_geracao"
        >
          <div
            className="absolute inset-0 bg-foreground-900/50"
            onClick={() => setConfirmandoGeracao(false)}
            aria-hidden="true"
          ></div>
          <div className="relative bg-background-50 rounded-lg border border-background-200/70 w-full max-w-md shadow-lg">
            <div className="p-6 pb-4">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                  <i className="ri-alert-line text-amber-600 text-xl"></i>
                </div>
                <div className="min-w-0">
                  <h3
                    id="titulo_confirmar_geracao"
                    className="text-base font-semibold text-foreground-900"
                  >
                    Revise os dados, pois as unidades só podem ser geradas uma única vez
                  </h3>
                  <p className="text-sm text-foreground-600 mt-1.5">
                    Confira a estrutura antes de confirmar. Depois de geradas, as unidades são
                    editadas ou removidas uma a uma, na aba Imóveis.
                  </p>
                </div>
              </div>
            </div>

            <dl className="px-6 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <dt className="text-foreground-500">Torre</dt>
              <dd className="text-foreground-800 font-medium text-right">
                {novaTorre.nome.trim()}
              </dd>
              <dt className="text-foreground-500">Pavimentos com unidades</dt>
              <dd className="text-foreground-800 font-medium text-right">
                {novaTorre.pavimentos_com_unidades} de {novaTorre.pavimentos}
              </dd>
              <dt className="text-foreground-500">Unidades por pavimento</dt>
              <dd className="text-foreground-800 font-medium text-right">
                {novaTorre.aptos_por_andar}
              </dd>
              <dt className="text-foreground-500">Total</dt>
              <dd className="text-foreground-900 font-semibold text-right">
                {resumo.total} unidade(s)
              </dd>
            </dl>

            <div className="px-6 mt-4">
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">
                Serão criadas
              </p>
              <div className="flex flex-wrap gap-1.5">
                {resumo.amostra.map((unidade, indice) => (
                  <span key={unidade}>
                    {resumo.truncada && indice === resumo.amostra.length - 2 && (
                      <span className="text-foreground-400 text-xs mr-1.5">…</span>
                    )}
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-background-100 text-xs font-medium text-foreground-700">
                      {unidade}
                    </span>
                  </span>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 pt-5">
              <button
                type="button"
                onClick={() => setConfirmandoGeracao(false)}
                disabled={salvando}
                className="px-4 py-2.5 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors disabled:opacity-60"
              >
                Revisar
              </button>
              <button
                type="button"
                onClick={() => void criarTorre(true)}
                disabled={salvando}
                className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60"
              >
                {salvando ? 'Gerando...' : `Gerar ${resumo.total} unidade(s)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteTarget(null)}></div>
          <div className="relative bg-background-50 rounded-lg border border-background-200/70 p-6 w-full max-w-md mx-4 shadow-lg">
            <p className="text-sm font-semibold text-foreground-900 mb-2">Confirmar exclusão</p>
            <p className="text-sm text-foreground-600 mb-6">
              Tem certeza que deseja excluir a <strong>{deleteTarget.nome}</strong>? Todos os
              andares configurados serão perdidos.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors whitespace-nowrap"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <div
            className={`px-4 py-3 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 ${
              toast.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : toast.type === 'error'
                  ? 'bg-red-50 text-red-800 border border-red-200'
                  : 'bg-blue-50 text-blue-800 border border-blue-200'
            }`}
          >
            <i
              className={`${toast.type === 'success' ? 'ri-checkbox-circle-line text-green-600' : toast.type === 'error' ? 'ri-close-circle-line text-red-600' : 'ri-information-line text-blue-600'}`}
            ></i>
            {toast.msg}
          </div>
        </div>
      )}

      <ContextualTour
        storageKey="tour_torres_cadastro"
        steps={torresTourSteps}
        title="Cadastro de Torres"
      />
    </div>
  );
}
